import React, { memo, useState } from 'react';
import {
    Box,
    ButtonBase,
    CircularProgress,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import TagFacesIcon from '@mui/icons-material/TagFaces';
import MoodBadIcon from '@mui/icons-material/MoodBad';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { MessageGroupInfoFull } from '../../type/chat.types';

type SatisfactionMood = 'happy' | 'unhappy';

type SatisfactionProps = {
    chatInfo: MessageGroupInfoFull;
    onSatisfaction: (mood: SatisfactionMood) => Promise<void>;
};

const Satisfaction = ({ chatInfo, onSatisfaction }: SatisfactionProps) => {
    const theme = useTheme();
    const [loadingMood, setLoadingMood] = useState<SatisfactionMood | null>(null);
    const selectedMood = chatInfo?.satisfaction as SatisfactionMood | null | undefined;

    const handleSelect = async (mood: SatisfactionMood) => {
        if (loadingMood || selectedMood) return;

        setLoadingMood(mood);
        try {
            await onSatisfaction(mood);
        } catch {
            // The action hook shows the API error to the user.
        } finally {
            setLoadingMood(null);
        }
    };

    const renderMoodButton = (
        mood: SatisfactionMood,
        label: string,
        Icon: typeof TagFacesIcon,
    ) => {
        const selected = selectedMood === mood;
        const isHappy = mood === 'happy';
        const accent = isHappy ? theme.palette.success.main : theme.palette.error.main;

        return (
            <ButtonBase
                onClick={() => handleSelect(mood)}
                disabled={Boolean(loadingMood || selectedMood)}
                aria-pressed={selected}
                sx={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: 58,
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: selected ? accent : 'divider',
                    bgcolor: selected ? alpha(accent, 0.1) : 'background.paper',
                    color: selected ? accent : 'text.secondary',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.85,
                    transition: 'all .15s ease',
                    '&:hover': {
                        borderColor: accent,
                        bgcolor: alpha(accent, selected ? 0.13 : 0.06),
                    },
                    '&.Mui-disabled': {
                        opacity: 0.72,
                    },
                }}>
                {loadingMood === mood ? (
                    <CircularProgress size={22} color="inherit" />
                ) : (
                    <Icon sx={{ fontSize: 27 }} />
                )}
                <Typography
                    sx={{
                        fontSize: 13,
                        fontWeight: selected ? 800 : 600,
                        color: 'inherit',
                    }}>
                    {label}
                </Typography>
                {selected && loadingMood !== mood && (
                    <CheckCircleIcon sx={{ fontSize: 17 }} />
                )}
            </ButtonBase>
        );
    };

    return (
        <Box
            sx={{
                flexShrink: 0,
                px: { xs: 1.25, md: 2 },
                py: 1.5,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
            }}>
            <Box
                sx={{
                    width: '100%',
                    maxWidth: 520,
                    mx: 'auto',
                }}>
                <Typography
                    className="font-yekan-heavy"
                    sx={{
                        fontSize: { xs: 13.5, md: 14.5 },
                        textAlign: 'center',
                        color: 'text.primary',
                        mb: 1,
                    }}>
                    از پاسخ‌گویی به این تیکت رضایت داشتید؟
                </Typography>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    {renderMoodButton('happy', 'راضی بودم', TagFacesIcon)}
                    {renderMoodButton('unhappy', 'ناراضی بودم', MoodBadIcon)}
                </Box>
            </Box>
        </Box>
    );
};

export default memo(Satisfaction);
